export interface Character {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly bio: string;
  readonly accentColor: string;
}

export const DEFAULT_ACCENT_COLOR = '#c4ad88';

export class CharacterParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CharacterParseError';
  }
}

export class CharacterRoster {
  readonly all: readonly Character[];
  private readonly byId: ReadonlyMap<string, Character>;

  constructor(characters: readonly Character[]) {
    this.all = characters;
    this.byId = new Map(characters.map((character) => [character.id, character]));
  }

  static fromJson(json: unknown, source = 'characters.json'): CharacterRoster {
    const record = asRecord(json, source);
    const entries = record['characters'];
    if (!Array.isArray(entries) || entries.length === 0) {
      throw new CharacterParseError(`${source}: "characters" must be a non-empty array`);
    }
    return new CharacterRoster(
      entries.map((entry, index) => characterFromJson(entry, `${source} › characters[${index}]`)),
    );
  }

  get(id: string): Character | undefined {
    return this.byId.get(id.toLowerCase());
  }

  has(id: string): boolean {
    return this.byId.has(id.toLowerCase());
  }

  nameOf(id: string, fallback: string): string {
    return this.get(id)?.name ?? fallback;
  }

  accentOf(id: string): string {
    return this.get(id)?.accentColor ?? DEFAULT_ACCENT_COLOR;
  }
}

export const EMPTY_ROSTER = new CharacterRoster([]);

export function portraitFileName(speakerId: string): string {
  return `${speakerId.toLowerCase()}.png`;
}

export function initialOf(displayName: string): string {
  const first = displayName.trim().charAt(0);
  return first === '' ? '?' : first.toUpperCase();
}

function characterFromJson(json: unknown, source: string): Character {
  const record = asRecord(json, source);
  return {
    id: requiredString(record, 'id', source).toLowerCase(),
    name: requiredString(record, 'name', source),
    role: requiredString(record, 'role', source),
    bio: requiredString(record, 'bio', source),
    accentColor: colorOrDefault(record['accentColor']),
  };
}

function colorOrDefault(value: unknown): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : DEFAULT_ACCENT_COLOR;
}

function asRecord(value: unknown, source: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new CharacterParseError(`${source}: expected an object`);
  }
  return value as Record<string, unknown>;
}

function requiredString(record: Record<string, unknown>, key: string, source: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new CharacterParseError(`${source}: "${key}" must be a non-empty string`);
  }
  return value;
}
